// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2024 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import "@fontsource/libre-baskerville";
import "@fontsource/public-sans/400.css";
import "@fontsource/public-sans/500.css";
import "@fontsource/public-sans/600.css";
import "@fontsource/public-sans/700.css";
import "./global.css";
// eslint-disable-next-line @nx/enforce-module-boundaries
import "../../../../../libs/@jii/common-ui/src/global.css";

import {
  GlobalStyle as GlobalStyleBase,
  typography,
} from "@recidiviz/design-system";
import { ErrorBoundary, withSentryReactRouterV6Routing } from "@sentry/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { Route, Routes } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import styled, { createGlobalStyle } from "styled-components";

import { NotFound } from "~@jii/common-ui";
import { queryClient, StoreProvider } from "~@jii/data";
import { ErrorPage, GenericLayoutRoute } from "~@jii/layout";
import {
  AfterLogin,
  EdovoLandingPage,
  EmailVerification,
  OrijinSSOPage,
  SiteRoot,
  State,
  StateSelect,
} from "~@jii/paths";
import { palette } from "~design-system";

import { PageAfterLogin } from "../pages/PageAfterLogin";
import { PageEdovoLanding } from "../pages/PageEdovoLanding";
import { PageHome } from "../pages/PageHome";
import { PageOrijinSSO } from "../pages/PageOrijinSSO";
import { PageResidentsRoot } from "../pages/PageResidentsRoot";
import { PageRoot } from "../pages/PageRoot";
import { PageSearch } from "../pages/PageSearch";
import { PageSelectState } from "../pages/PageSelectState";
import { PageSingleResidentRoot } from "../pages/PageSingleResidentRoot";
import { PageState } from "../pages/PageState";
import { PageVerifyEmail } from "../pages/PageVerifyEmail";
import { ResidentsLayoutRoute } from "../ResidentsLayoutRoute/ResidentsLayoutRoute";
import { SkeletonThemeProvider } from "../SkeletonThemeProvider/SkeletonThemeProvider";
import { StateSpecificRouter } from "../StateSpecificRouter/StateSpecificRouter";
import { TranslationProvider } from "../TranslationProvider/TranslationProvider";
import { initApp } from "./initApp";

const StyledApp = styled.div`
  /* these properties prevent full-bleed sections from messing up the page width */
  min-height: 100vh;
  overflow-x: clip;
`;

const GlobalStyle = createGlobalStyle`
  body {
    ${typography.Sans16}

    background: ${palette.white};
    color: ${palette.pine2};
  }
`;

initApp();

// doing this once (at the root off all <Routes>) allows Sentry to trace client-side URLs
const SentryRoutes = withSentryReactRouterV6Routing(Routes);

export function App() {
  return (
    <ErrorBoundary fallback={ErrorPage}>
      <StoreProvider>
        <QueryClientProvider client={queryClient}>
          <TranslationProvider>
            <GlobalStyleBase />
            <GlobalStyle />
            <SkeletonThemeProvider>
              <StyledApp>
                <SentryRoutes>
                  <Route path={SiteRoot.path} element={<PageRoot />}>
                    <Route index element={<PageHome />} />
                    <Route path={State.path}>
                      <Route index element={<PageState />} />
                      <Route element={<PageResidentsRoot />}>
                        <Route
                          path={State.Resident.path}
                          element={<PageSingleResidentRoot />}
                        >
                          <Route element={<ResidentsLayoutRoute />}>
                            <Route
                              index
                              path="*"
                              element={<StateSpecificRouter />}
                            />
                          </Route>
                        </Route>
                        <Route element={<ResidentsLayoutRoute />}>
                          <Route
                            path={State.Search.path}
                            element={<PageSearch />}
                          />
                        </Route>
                      </Route>
                    </Route>
                    <Route element={<GenericLayoutRoute />}>
                      <Route
                        path={EmailVerification.path}
                        element={<PageVerifyEmail />}
                      />
                      <Route
                        path={AfterLogin.path}
                        element={<PageAfterLogin />}
                      />
                      <Route
                        path={EdovoLandingPage.path}
                        element={<PageEdovoLanding />}
                      />
                      <Route
                        path={StateSelect.path}
                        element={<PageSelectState />}
                      />

                      <Route path="*" element={<NotFound />} />
                    </Route>
                    <Route
                      path={OrijinSSOPage.path}
                      element={<PageOrijinSSO />}
                    />
                  </Route>
                </SentryRoutes>
              </StyledApp>
              <ToastContainer />
            </SkeletonThemeProvider>
          </TranslationProvider>
        </QueryClientProvider>
      </StoreProvider>
    </ErrorBoundary>
  );
}
